import { AUTH_SESSION_FAILURE_LOG_REASON, isHttpStatusAuthSessionFailure } from "@Front/Api/helpers/httpAuthSessionFailurePolicy";
import {
	tryRecoverSessionViaRefresh,
	type SessionRefreshRecoveryOutcome,
} from "@Front/Api/helpers/authSessionRefreshRecoveryHelpers";
import {
	resolveActiveAuthContextFromSession,
	resolveAuthContextForEndpoint,
} from "@Front/Api/helpers/authTokenResolutionHelpers";
import { resolveLiveAuthContextIgnoringRoute } from "@Front/Api/helpers/authLiveSessionContextHelpers";
import { shouldTeardownSessionOnAuthFailure } from "@Front/Api/helpers/authFailureTeardownPolicy";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { SessionRefreshService } from "@Front/Services/SessionRefreshService/SessionRefreshService";
import type { AnyJwtPayload } from "@Front/Types/JwtPayloads";
import { getLeCoffreRefreshTokenCookieForEndpoint, type LeCoffreAuthContext } from "@Front/Utils/lecoffreSessionCookieHelpers";
import { jwtDecode } from "@Front/Utils/jwtDecodeWrapper";
import { isAuthFlowRoute, resolveRouteContextFamily } from "@Front/Utils/routeContextRegistry";

function getBrowserPathname(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	return window.location.pathname;
}

function doesEndpointContextMatchCurrentRouteFamily(endpointContext: LeCoffreAuthContext): boolean {
	const routeFamily = resolveRouteContextFamily(getBrowserPathname());
	if (routeFamily === "notary") {
		return endpointContext === "notary";
	}
	if (routeFamily === "client") {
		return endpointContext === "customer" || endpointContext === "thirdparty";
	}
	return false;
}

/**
 * Whether a 401 on this endpoint should trigger session teardown / login redirect.
 * Cross-context 401s are ignored when another family is active, including a live
 * customer session on a `/folders` URL (route-scoped activeContext is then null).
 * When no session is active, still recover if the call matches the current protected route family
 * (avoids zombie SPA after cookies cleared — F5 with activeContext null).
 */
export function shouldHandleAuthFailureForEndpoint(endpoint: string | undefined): boolean {
	if (endpoint?.includes("/auth/logout")) {
		return false;
	}
	if (isAuthFlowRoute(getBrowserPathname())) {
		return false;
	}
	const endpointContext = resolveAuthContextForEndpoint(endpoint);
	return shouldTeardownSessionOnAuthFailure({
		endpointContext,
		routeScopedActiveContext: resolveActiveAuthContextFromSession(),
		liveSessionContext: resolveLiveAuthContextIgnoringRoute(),
		endpointMatchesRouteFamily: doesEndpointContextMatchCurrentRouteFamily(endpointContext),
	});
}

async function triggerSessionFailureFromRefreshToken(
	refreshToken: string,
	httpStatus: number,
	endpoint: string | undefined,
	logPrefix: string,
): Promise<boolean> {
	try {
		const payload = jwtDecode<AnyJwtPayload>(refreshToken);
		if (!payload) {
			return false;
		}
		LoggerService.error(`❌ [${logPrefix}] Critical error detected, triggering session failure`, {
			httpStatus,
			reason: AUTH_SESSION_FAILURE_LOG_REASON,
			endpoint: endpoint ?? null,
		});
		await SessionRefreshService.getInstance().handleSessionFailure(payload);
		return true;
	} catch (error) {
		LoggerService.warn(`⚠️ [${logPrefix}] Unable to decode refresh token for session failure`, { error });
		return false;
	}
}

function shouldAbortAfterRecovery(
	recovery: SessionRefreshRecoveryOutcome,
	httpStatus: number,
	endpoint: string | undefined,
	logPrefix: string,
): boolean {
	if (recovery === "recovered") {
		return true;
	}
	if (recovery !== "transient") {
		return false;
	}
	LoggerService.warn(`⚠️ [${logPrefix}] Auth failure recovery deferred — transient refresh failure`, {
		httpStatus,
		endpoint: endpoint ?? null,
		activeContext: resolveActiveAuthContextFromSession(),
	});
	return true;
}

async function teardownAfterConfirmedAuthFailure(
	httpStatus: number,
	endpoint: string | undefined,
	logPrefix: string,
): Promise<void> {
	const refreshToken = getLeCoffreRefreshTokenCookieForEndpoint(endpoint);
	if (refreshToken) {
		const handled = await triggerSessionFailureFromRefreshToken(refreshToken, httpStatus, endpoint, logPrefix);
		if (handled) {
			return;
		}
	}
	const endpointContext = resolveAuthContextForEndpoint(endpoint);
	LoggerService.error(`❌ [${logPrefix}] Auth failure without usable refresh token — redirecting`, {
		httpStatus,
		endpoint: endpoint ?? null,
		endpointContext,
		activeContext: resolveActiveAuthContextFromSession(),
	});
	await SessionRefreshService.getInstance().handleUnauthenticatedAccess(endpointContext);
}

/**
 * On 401 for the active (or route-matched) auth context: try refresh recovery first, then teardown.
 * Ignores stale failures when a usable access token is already present after cookie preference.
 */
export async function handleAuthSessionFailureForEndpoint(
	httpStatus: number,
	endpoint: string | undefined,
	logPrefix: string,
): Promise<void> {
	if (typeof window === "undefined" || !isHttpStatusAuthSessionFailure(httpStatus)) {
		return;
	}
	if (!shouldHandleAuthFailureForEndpoint(endpoint)) {
		LoggerService.warn(`⚠️ [${logPrefix}] Ignoring auth failure for non-active auth context`, {
			httpStatus,
			endpoint: endpoint ?? null,
			activeContext: resolveActiveAuthContextFromSession(),
			endpointContext: resolveAuthContextForEndpoint(endpoint),
		});
		return;
	}
	const recovery = await tryRecoverSessionViaRefresh(endpoint, logPrefix);
	if (shouldAbortAfterRecovery(recovery, httpStatus, endpoint, logPrefix)) {
		return;
	}
	await teardownAfterConfirmedAuthFailure(httpStatus, endpoint, logPrefix);
}
